import { useEffect, useMemo, useState } from "react";
import { CaretUpDown, Check, ArrowClockwise } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import api from "@/lib/api";

/**
 * Searchable master-list combobox for parts.
 *
 * Props:
 *  - value: { name, stock_code, group, uom } — currently selected item ("" name = empty)
 *  - onChange(item): fires when a stock item OR a free-text override is picked
 *  - testid: base test-id (e.g. "part-picker-0")
 *
 * Stock items are grouped by `group`. Users can also type a custom name and
 * pick the "Use custom part …" option at the bottom of the list.
 */
const CACHE_TTL_MS = 60_000;   // refresh in-memory master list every minute
let STOCK_CACHE = null;
let STOCK_CACHE_AT = 0;
let STOCK_INFLIGHT = null;

async function loadStock(force = false) {
  const fresh = STOCK_CACHE && Date.now() - STOCK_CACHE_AT < CACHE_TTL_MS && !force;
  if (fresh) return STOCK_CACHE;
  if (STOCK_INFLIGHT) return STOCK_INFLIGHT;
  STOCK_INFLIGHT = api.get("/stock", { params: { limit: 10000 } }).then((r) => {
    STOCK_CACHE = r.data || [];
    STOCK_CACHE_AT = Date.now();
    STOCK_INFLIGHT = null;
    return STOCK_CACHE;
  }).catch((e) => {
    STOCK_INFLIGHT = null;
    throw e;
  });
  return STOCK_INFLIGHT;
}

export default function StockPartCombobox({ value, onChange, testid = "stock-part", placeholder = "Search part…" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState(STOCK_CACHE || []);
  const [loading, setLoading] = useState(!STOCK_CACHE);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let alive = true;
    // Refresh in the background if cache is stale (or missing).
    loadStock(false)
      .then((data) => { if (alive) setItems(data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const data = await loadStock(true);
      setItems(data);
    } finally {
      setRefreshing(false);
    }
  };

  // Filter locally; grouped for display.
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map();
    for (const it of items) {
      if (q && !`${it.item} ${it.description}`.toLowerCase().includes(q)) continue;
      const arr = map.get(it.group) || [];
      arr.push(it);
      map.set(it.group, arr);
    }
    // Show all matches when the user is searching; cap only when browsing
    // the full unfiltered list (keeps the initial render snappy).
    const isSearching = q.length > 0;
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([g, arr]) => [g, isSearching ? arr : arr.slice(0, 30), arr.length]);
  }, [items, query]);

  const pickStock = (it) => {
    onChange({
      name: it.item,
      stock_code: it.item,
      description: it.description,
      group: it.group,
      uom: it.uom,
    });
    setOpen(false);
    setQuery("");
  };

  const pickCustom = () => {
    const name = query.trim();
    if (!name) return;
    onChange({ name, stock_code: null, group: "Custom", uom: null, description: name });
    setOpen(false);
    setQuery("");
  };

  const label = value?.name || "";
  const isCustom = value?.name && !value?.stock_code;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={`${testid}-trigger`}
          className="w-full h-9 border border-neutral-400 rounded-none flex items-center justify-between gap-2 px-2 text-left bg-white hover:border-neutral-900 focus:outline-none focus:border-neutral-900 focus:ring-0"
        >
          <span className={`truncate text-sm flex-1 ${label ? "" : "text-neutral-400"}`}>
            {label || placeholder}
          </span>
          {isCustom && (
            <span className="text-[9px] font-mono uppercase tracking-wider bg-neutral-200 text-neutral-700 px-1 py-0.5">
              custom
            </span>
          )}
          {value?.group && !isCustom && (
            <span className="text-[9px] font-mono uppercase tracking-wider bg-neutral-900 text-neutral-50 px-1 py-0.5 truncate max-w-[80px]" title={value.group}>
              {value.group}
            </span>
          )}
          <CaretUpDown size={14} weight="bold" className="text-neutral-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[380px] p-0 rounded-none border border-neutral-900 shadow-lg"
        data-testid={`${testid}-popover`}
      >
        <Command shouldFilter={false} className="rounded-none">
          <div className="border-b border-neutral-200 flex items-center">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder={`Search ${items.length ? items.length.toLocaleString() : "2 000+"} parts by name or description…`}
              className="h-10 text-sm flex-1"
              data-testid={`${testid}-input`}
            />
            <button
              type="button"
              onClick={refresh}
              disabled={refreshing}
              title="Refresh master parts list"
              data-testid={`${testid}-refresh`}
              className="h-10 w-10 flex items-center justify-center border-l border-neutral-200 hover:bg-neutral-100 disabled:opacity-40"
            >
              <ArrowClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
          <CommandList className="max-h-[320px]">
            {loading && (
              <div className="py-8 text-center text-xs text-neutral-500">Loading master list…</div>
            )}
            {!loading && grouped.length === 0 && (
              <CommandEmpty className="py-4 text-xs text-neutral-500">
                No match in master list.
              </CommandEmpty>
            )}
            {!loading && grouped.map(([group, arr, total]) => (
              <CommandGroup
                key={group}
                heading={
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{group}</span>
                    <span className="text-[9px] font-mono text-neutral-400">{total}</span>
                  </div>
                }
                className="px-2"
              >
                {arr.map((it) => {
                  const selected = value?.stock_code === it.item;
                  return (
                    <CommandItem
                      key={it.id}
                      value={`${it.item}||${it.id}`}
                      onSelect={() => pickStock(it)}
                      data-testid={`${testid}-option-${it.id}`}
                      className="rounded-none px-2 py-1.5 cursor-pointer aria-selected:bg-neutral-900 aria-selected:text-white"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{it.item}</div>
                        {it.description && it.description !== it.item && (
                          <div className="text-[10px] text-neutral-500 truncate">{it.description}</div>
                        )}
                      </div>
                      {selected && <Check size={14} weight="bold" className="ml-2 shrink-0" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
            {query.trim() && (
              <div className="border-t border-neutral-200">
                <CommandItem
                  value="__custom__"
                  onSelect={pickCustom}
                  data-testid={`${testid}-custom`}
                  className="rounded-none px-3 py-2 cursor-pointer aria-selected:bg-amber-100"
                >
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 text-[9px]">Custom</span>
                    <span className="truncate">Use “{query.trim()}” as custom part</span>
                  </div>
                </CommandItem>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
